'use client'

import React, { useState, useEffect, useRef } from 'react'
import { cn } from '../lib/utils'
import { colors } from '../tokens/colors'
import { LucideIcon, ChevronDown, Check, Search, Plus } from 'lucide-react'
import { DropdownPortal, useDropdownPosition } from './DropdownPortal'

// Tipos base do AsyncSelect
type AsyncSelectSize = 'sm' | 'md' | 'lg'
type AsyncSelectVariant = 'default' | 'error' | 'success'

// Interface para opções
export interface AsyncSelectOption {
  value: string | number
  label: string
  disabled?: boolean
  icon?: LucideIcon
  data?: any // Dados adicionais da opção
}

// Interface para aparência
interface AsyncSelectAppearance {
  backgroundColor?: string
  textColor?: string
  borderColor?: string
  placeholderColor?: string
}

interface AsyncSelectProps {
  // Busca assíncrona
  onSearch: (query: string) => Promise<AsyncSelectOption[]>
  
  // Configuração da busca
  debounceMs?: number
  minSearchLength?: number
  
  // Seleção
  value?: string | number
  onChange?: (value: string | number, option: AsyncSelectOption) => void
  
  // Configuração visual
  variant?: AsyncSelectVariant
  size?: AsyncSelectSize
  
  // Estados
  error?: boolean
  success?: boolean
  disabled?: boolean
  
  // Ícones
  leftIcon?: LucideIcon
  
  // Labels e mensagens
  label?: string
  helperText?: string
  errorMessage?: string
  placeholder?: string
  searchPlaceholder?: string
  
  // Customização
  appearance?: AsyncSelectAppearance
  
  // Layout
  fullWidth?: boolean
  className?: string
  
  // Comportamento
  clearable?: boolean
  required?: boolean
  
  // Callback para criar nova opção
  onCreateNew?: (query: string) => void
  createNewLabel?: string
  
  // Renderização customizada
  renderOption?: (option: AsyncSelectOption, isSelected: boolean) => React.ReactNode
}

// Presets de variantes
const variantPresets: Record<AsyncSelectVariant, AsyncSelectAppearance> = {
  default: {
    backgroundColor: colors.background.white,
    textColor: colors.text.primary,
    borderColor: colors.border.primary,
    placeholderColor: colors.text.tertiary,
  },
  error: {
    backgroundColor: colors.status.error.background,
    textColor: colors.status.error.text,
    borderColor: colors.status.error.text,
    placeholderColor: colors.text.tertiary,
  },
  success: {
    backgroundColor: colors.status.success.background,
    textColor: colors.status.success.text,
    borderColor: colors.status.success.text,
    placeholderColor: colors.text.tertiary,
  },
}

const AsyncSelect = React.forwardRef<HTMLDivElement, AsyncSelectProps>(
  ({
    onSearch,
    debounceMs = 300,
    minSearchLength = 2,
    value,
    onChange,
    variant = 'default',
    size = 'md',
    error = false,
    success = false,
    disabled = false,
    leftIcon: LeftIcon,
    label,
    helperText,
    errorMessage,
    placeholder = "Selecione uma opção...",
    searchPlaceholder = "Buscar...",
    appearance,
    fullWidth = false,
    className,
    clearable = false,
    required = false,
    onCreateNew,
    createNewLabel = "Criar novo",
    renderOption,
    ...props
  }, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [options, setOptions] = useState<AsyncSelectOption[]>([])
    const [selectedOption, setSelectedOption] = useState<AsyncSelectOption | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)

    const containerRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const dropdownPosition = useDropdownPosition(containerRef, isOpen && showDropdown)

    // Determinar o estado visual
    const currentVariant = error ? 'error' : success ? 'success' : variant
    const currentAppearance = { ...variantPresets[currentVariant], ...appearance }

    // Classes baseadas no tamanho (alinhado com outros componentes)
    const sizeClasses = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base', 
      lg: 'px-5 py-4 text-lg'
    }

    // Busca assíncrona com debounce
    useEffect(() => {
      const searchOptions = async () => {
        if (searchTerm.length < minSearchLength) {
          setOptions([])
          // Só mostrar dropdown se estiver aberto E tem algum texto
          if (isOpen && searchTerm.length > 0) {
            setShowDropdown(true)
          } else {
            setShowDropdown(false)
          }
          return
        }

        // Se já tem uma opção selecionada e o searchTerm é igual ao label dela, não buscar
        if (selectedOption && searchTerm === selectedOption.label) {
          setShowDropdown(false)
          return
        }

        // Só buscar se o componente estiver aberto
        if (!isOpen) {
          return
        }

        setIsLoading(true)
        setShowDropdown(true) // Mostrar dropdown antes de buscar
        
        try {
          const results = await onSearch(searchTerm)
          setOptions(results)
          // Manter dropdown aberto após busca bem-sucedida
          setShowDropdown(true)
        } catch (error) {
          console.error('Erro na busca:', error)
          setOptions([])
          setShowDropdown(true) // Mostrar dropdown mesmo com erro
        } finally {
          setIsLoading(false)
        }
      }

      const debounceTimer = setTimeout(searchOptions, debounceMs)
      return () => clearTimeout(debounceTimer)
    }, [searchTerm, onSearch, debounceMs, minSearchLength, selectedOption, isOpen])

    // Fechar ao clicar fora
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node
        // The dropdown lives in a portal under document.body — it must count
        // as "inside" for the outside-click check.
        if (
          containerRef.current && !containerRef.current.contains(target) &&
          !dropdownRef.current?.contains(target)
        ) {
          setIsOpen(false)
          setShowDropdown(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Atualizar selectedOption quando value muda externamente
    useEffect(() => {
      if (value) {
        // Se há um value, encontrar a opção correspondente nas opções atuais
        if (options.length > 0) {
          const option = options.find(opt => opt.value === value)
          if (option) {
            setSelectedOption(option)
            setSearchTerm(option.label)
          }
        }
      } else if (!value && selectedOption) {
        // Só limpar se value foi realmente limpo externamente
        setSelectedOption(null)
        setSearchTerm('')
      }
    }, [value])

    const handleSelect = (option: AsyncSelectOption) => {
      if (option.disabled) return
      
      setSelectedOption(option)
      setSearchTerm('') // Limpar o campo após seleção
      setIsOpen(false)
      setShowDropdown(false)
      setOptions([]) // Limpar resultados
      
      // Tirar o foco do input
      searchInputRef.current?.blur()
      
      onChange?.(option.value, option)
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setSearchTerm(newValue)
      
      // Abrir dropdown quando começar a digitar
      if (!isOpen) {
        setIsOpen(true)
      }
      
      if (selectedOption && newValue !== selectedOption.label) {
        // Usuário está editando, desmarcar seleção
        setSelectedOption(null)
        onChange?.('' as any, null as any)
      }
    }

    const handleCreateNew = () => {
      if (onCreateNew) {
        setIsOpen(false)
        setShowDropdown(false)
        onCreateNew(searchTerm)
      }
    }

    // Enquanto aberto, o input carrega o texto da busca; fechado, mostra a opção
    // escolhida. Sem isso a seleção some do campo (searchTerm é limpo ao
    // selecionar) e o usuário fica sem retorno visual do que escolheu.
    const displayValue = isOpen ? searchTerm : (selectedOption?.label ?? searchTerm)

    return (
      <div
        ref={ref}
        className={cn('relative', fullWidth && 'w-full', className)}
        {...props}
      >
        {/* Label */}
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Select Container */}
        <div ref={containerRef} className="relative">
          <div
            className={cn(
              'w-full border-2 rounded-lg transition-all duration-200 cursor-text',
              sizeClasses[size],
              disabled && 'opacity-50 cursor-not-allowed',
              fullWidth && 'w-full'
            )}
            style={{
              backgroundColor: currentAppearance.backgroundColor,
              borderColor: currentAppearance.borderColor,
              color: currentAppearance.textColor,
            }}
            onClick={() => {
              if (!disabled) {
                setIsOpen(true)
                searchInputRef.current?.focus()
              }
            }}
          >
            <div className="flex items-center h-full">
              {/* Left Icon */}
              {LeftIcon && (
                <LeftIcon className="w-5 h-5 mr-2 flex-shrink-0" style={{ color: currentAppearance.textColor }} />
              )}

              {/* Input/Display */}
              <input
                ref={searchInputRef}
                type="text"
                value={displayValue}
                onChange={handleSearchChange}
                onFocus={() => {
                  setIsOpen(true)
                  // Se tem texto suficiente, mostrar dropdown
                  if (searchTerm.length >= minSearchLength) {
                    setShowDropdown(true)
                  }
                }}
                placeholder={placeholder}
                disabled={disabled}
                className="flex-1 bg-transparent outline-none"
                style={{ 
                  color: currentAppearance.textColor,
                }}
              />

              {/* Loading indicator */}
              {isLoading && (
                <div className="w-4 h-4 border-2 border-gray-300 border-t-brand-primary rounded-full animate-spin mr-2" />
              )}

              {/* Chevron */}
              <ChevronDown 
                className={cn(
                  'w-5 h-5 transition-transform duration-200 flex-shrink-0',
                  isOpen && 'transform rotate-180'
                )}
                style={{ color: currentAppearance.textColor }}
              />
            </div>
          </div>

          {/* Dropdown — portal on document.body so no Modal/overflow clips it */}
          {isOpen && showDropdown && (
            <DropdownPortal
              position={dropdownPosition}
              portalRef={dropdownRef}
              className="bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden"
            >
              {/* Options List */}
              <div className="max-h-60 overflow-y-auto" role="listbox">
                {isLoading ? (
                  <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-brand-primary rounded-full animate-spin" />
                    Buscando...
                  </div>
                ) : (
                  <>
                    {/* Mensagem quando não tem caracteres suficientes */}
                    {searchTerm.length < minSearchLength && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        Digite pelo menos {minSearchLength} caracteres para buscar
                      </div>
                    )}

                    {/* Opções encontradas */}
                    {searchTerm.length >= minSearchLength && options.length > 0 && (
                      <>
                        {options.map((option) => {
                          const isSelected = option.value === value
                          const IconComponent = option.icon

                          return (
                            <div
                              key={option.value}
                              className={cn(
                                'px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 transition-colors',
                                isSelected && 'bg-brand-primary/10 text-brand-primary',
                                option.disabled && 'opacity-50 cursor-not-allowed'
                              )}
                              onClick={() => handleSelect(option)}
                              role="option"
                              aria-selected={isSelected}
                            >
                              {renderOption ? (
                                renderOption(option, isSelected)
                              ) : (
                                <>
                                  <div className="flex items-center gap-2">
                                    {IconComponent && <IconComponent className="w-4 h-4" />}
                                    <span className="truncate">{option.label}</span>
                                  </div>
                                  {isSelected && <Check className="w-4 h-4" />}
                                </>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}

                    {/* Nenhuma opção encontrada */}
                    {searchTerm.length >= minSearchLength && options.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        Nenhuma opção encontrada
                      </div>
                    )}
                    
                    {/* Create New Option - sempre mostrar quando tem texto suficiente */}
                    {onCreateNew && searchTerm.length >= minSearchLength && (
                      <div
                        className="px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-gray-50 transition-colors border-t border-gray-100 text-brand-primary"
                        onClick={handleCreateNew}
                      >
                        <Plus className="w-4 h-4" />
                        <span>{createNewLabel} "{searchTerm}"</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </DropdownPortal>
          )}
        </div>

        {/* Helper Text */}
        {(helperText || errorMessage) && (
          <p className={cn(
            'mt-1 text-sm',
            error ? 'text-red-600' : 'text-gray-500'
          )}>
            {error ? errorMessage : helperText}
          </p>
        )}
      </div>
    )
  }
)

AsyncSelect.displayName = 'AsyncSelect'

export { AsyncSelect }